/* eslint-disable react/jsx-wrap-multilines */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { FormHandles } from '@unform/core';

import { useSnackbar } from 'notistack';

import { Fade } from '@material-ui/core';

import { ProfilesTable, CreateProfileModal } from './components';

import { PageContainer, Search } from '../../../app/styles';

import CreateIcon from '../../../assets/svg/createIcon';
import SearchBox from '../../../components/Tools/Search';
import LoaderPda from '../../../components/Tools/LoaderPda';
import FixedHeader from '../../../components/Tools/Breadcrumb';

import { IProfileProps, ItemList } from '../../../utils/interfaces';
import { searchBoxRequest, getOptionsAPIs } from './api/profile.api';

const Profile: React.FC = () => {
  const searchRef = useRef<FormHandles>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disableInput, setDisableInput] = useState<boolean>(false);

  const [profiles, setProfiles] = useState<IProfileProps[]>([]);
  const [clients, setClients] = useState<ItemList[]>([]);

  const [isCreateProfileModalOpen, setIsCreateProfileModalOpen] =
    useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const clientId = Number(localStorage.getItem('@pdamodules::codigoCliente'));

  const traductorMsg = (msg: string) => {
    return i18n.t(msg);
  };

  const handleGetSystemSelected = useCallback(() => {
    const isNotAdmin = String(clientId) !== '0';

    if (isNotAdmin) {
      const filterClient = clients.find(
        p => String(p.value) === String(clientId)
      );

      searchRef.current?.setFieldValue('cliente', {
        value: filterClient?.value,
        label: filterClient?.label,
      });
    }

    setDisableInput(isNotAdmin);
  }, [clientId, clients]);

  const hanbleGetOptions = useCallback(async () => {
    setIsLoading(true);

    await getOptionsAPIs()
      .then(res => {
        const clientsOptions = res[0];

        setClients(clientsOptions);
      })
      .catch(() => {
        enqueueSnackbar('Erro ao carregar dorps', {
          variant: 'error',
        });
      })
      .finally(() => setIsLoading(false));
  }, [enqueueSnackbar]);

  const handleGetProfiles = useCallback(
    async (data?: object) => {
      setIsLoading(true);

      await searchBoxRequest(data, clientId)
        .then(res => {
          if (!res.data.length) {
            setProfiles([]);
            enqueueSnackbar(traductorMsg('screens.profile.msgNoProfile'), {
              variant: 'warning',
            });
            return;
          }

          setProfiles(res.data);
          enqueueSnackbar(
            `${res.data.length} ${traductorMsg('screens.user.resultFound')}`,
            {
              variant: 'success',
            }
          );
        })
        .catch(err => {
          enqueueSnackbar(
            `${traductorMsg('screens.user.error')}: ${err.message}`,
            {
              variant: 'error',
            }
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [clientId, enqueueSnackbar]
  );

  useEffect(() => {
    hanbleGetOptions();
  }, [hanbleGetOptions]);

  useEffect(() => {
    handleGetSystemSelected();
  }, [handleGetSystemSelected]);

  return (
    <>
      <FixedHeader title={t('screens.profile.title')} />
      <Fade in timeout={1000} unmountOnExit>
        <PageContainer>
          <Search>
            <SearchBox
              searchBoxRef={searchRef}
              inputs={[
                {
                  name: 'descricaoPerfil',
                  label: String(t('screens.profile.description')),
                  type: 'text',
                  placeholder: String(t('screens.profile.description')),
                  xl: 4,
                  lg: 4,
                  xs: 12,
                  md: 4,
                  sm: 4,
                },
                {
                  name: 'cliente',
                  label: String(t('screens.profile.client')),
                  type: 'select',
                  options: clients,
                  isRequired: true,
                  isDisabled: disableInput,
                  placeholder: String(t('screens.profile.client')),
                  xl: 4,
                  lg: 4,
                  xs: 12,
                  md: 6,
                  sm: 6,
                },
                {
                  name: 'lider',
                  label: String(t('screens.profile.isLeader')),
                  type: 'switch',
                },
              ]}
              handleSubmit={data => handleGetProfiles(data)}
              createButton={() => setIsCreateProfileModalOpen(true)}
              createButtonContent={
                <>
                  <CreateIcon />
                  <p>{t('screens.profile.newProfile')}</p>
                </>
              }
              searchDisabled={isLoading}
            />
          </Search>
          {isLoading && <LoaderPda />}
        </PageContainer>
      </Fade>
      {!isLoading && (
        <ProfilesTable getProfiles={handleGetProfiles} rows={profiles} />
      )}
      {isCreateProfileModalOpen && (
        <CreateProfileModal
          isOpen={isCreateProfileModalOpen}
          handleClose={() => setIsCreateProfileModalOpen(false)}
          getProfiles={handleGetProfiles}
        />
      )}
    </>
  );
};

export default Profile;
